import { AfterViewInit, Component, OnInit, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { Cell, Grid, GridProperties, GridService } from '@app/shared/services/grid.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { Observable } from 'rxjs';
import { SharedModule } from '@app/shared/shared.module';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { EventsRepository } from '@app/shared/state/events/events.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-randomprize-minigame-modal',
  templateUrl: './randomprize-minigame.component.html',
  styleUrls: ['./randomprize-minigame.component.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class RandomPrizeMinigameModalComponent implements OnInit, AfterViewInit {
  componentDestroy: () => Observable<unknown>;

  protected readonly randomPrizeMinigameModalData = inject<Modal>(MAT_DIALOG_DATA);
  private eventManager = inject(EventManager);
  public logger = inject(LoggerService);
  private gridService = inject(GridService);
  private eventsRepo = inject(EventsRepository);

  NonPersistedModalName = NonPersistedModalName;

  event = signal<LimitedTimeEvent | null>(null);
  cookieSelectedCell = signal<Cell | null>(null);
  activateCursor = signal<boolean>(false);
  togglePercentAnimation = signal<boolean>(false);
  selectedIndex = signal<number | null>(null);

  grid: Grid;

  multiplierList = signal<number[]>([]);

  maxReward = computed(() => {
    const list = this.multiplierList();
    return list.length ? Math.max(...list) : 0;
  });

  preSortedReward: number;
  cookieSelectedPercent: number;
  manageIcons: string;

  ngOnInit(): void {
    const evt = this.eventsRepo.getEventById(this.randomPrizeMinigameModalData.data.properties.eventId);

    this.event.set(evt);

    this.multiplierList.set(this.randomPrizeMinigameModalData.data.properties.multiplierList);

    this.preSortedReward = this.randomPrizeMinigameModalData.data.properties.multiplier;

    this.grid = this.gridService.createGrid(this.getCustomProperties());

    setTimeout(() => {
      this.multiplierList.set([...this.multiplierList()].sort(() => Math.random() - 0.5));

      this.activateCursor.set(true);
    }, 7600);
  }

  ngAfterViewInit(): void {
    this.togglePercentAnimation.set(true);
  }

  openSelectedCookie(cell: Cell): void {
    this.activateCursor.set(false);
    this.togglePercentAnimation.set(false);
    this.cookieSelectedCell.set(cell);

    this.managePreDrawn(cell.index);
    this.selectedIndex.set(cell.index);

    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  manageIconsFn(cell: Cell, percent: number): string {
    const selected = this.cookieSelectedCell();
    const max = this.maxReward();

    if (selected && cell.index === selected.index) {
      return percent === max ? 'big-cookie-open' : 'cookie-open';
    } else if (selected && cell.index !== selected.index) {
      return percent === max ? 'unselected-big-cookie-open' : 'unselected-cookie-open';
    } else {
      return 'cookie-closed';
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(
      new Modal(this.randomPrizeMinigameModalData.id, ModalType.View, this.randomPrizeMinigameModalData)
    );
  }

  getCustomProperties(): GridProperties {
    const rows: number = this.multiplierList().length <= 10 ? 2 : 3;

    switch (this.multiplierList().length) {
      case 6:
      case 8:
      case 10:
      case 12:
      case 15:
      case 18:
        return this.createProperties(true, this.multiplierList().length / rows);

      case 5:
      case 7:
      case 9:
      case 11:
      case 14:
      case 17:
        return this.createProperties(false, Math.ceil(this.multiplierList().length / rows));

      case 13:
      case 16:
      case 19:
        return this.createProperties(false, Math.ceil(this.multiplierList().length / rows), true);

      default:
        return this.createProperties(false, this.multiplierList().length);
    }
  }

  createProperties(
    square: boolean,
    maxHorizontalElements: number,
    maxElementsInEvenRows: boolean = false
  ): GridProperties {
    return new GridProperties({
      totalElements: this.multiplierList().length,
      square,
      maxHorizontalElements,
      maxElementsInEvenRows,
    });
  }

  getStrValue(num: number): string {
    return `${num}%`;
  }

  private managePreDrawn(cookieSelectedPosition: number): void {
    const list = [...this.multiplierList()];

    const preDrawnIndex = list.findIndex((percent) => percent === this.preSortedReward);

    if (preDrawnIndex === -1) return;

    if (cookieSelectedPosition === preDrawnIndex) return;

    const temp = list[cookieSelectedPosition];
    list[cookieSelectedPosition] = list[preDrawnIndex];
    list[preDrawnIndex] = temp;

    this.multiplierList.set(list);
  }
}
